import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial } from '../utils/tracking';

/**
 * Reusable competitor-alternative landing page.
 * Routes: /zoominfo-alternative, /apollo-alternative,
 *         /lusha-alternative, /seamless-alternative
 */

const data = {
  zoominfo: {
    name: 'ZoomInfo',
    slug: 'zoominfo',
    tagline: 'Enterprise-Grade Data Without the Enterprise Price Tag',
    subtitle: 'ZoomInfo charges $15,000+/year with long-term contracts. kNine.ai delivers the same 100M+ contact database with AI-powered search starting at $6,000/year — no lock-in required.',
    painPoints: [
      { icon: '💸', title: 'Cut Your Data Spend by 60%', desc: 'ZoomInfo starts at $15K+/yr with mandatory annual contracts. kNine starts at $6K/yr with monthly payment options and no lock-in.' },
      { icon: '🤖', title: 'AI Search vs. Boolean Filters', desc: 'ZoomInfo still relies on traditional filters and boolean queries. kNine uses natural language AI — describe who you need and let Cooper & Roxi find them.' },
      { icon: '🧩', title: 'Staffing ATS Integrations', desc: 'ZoomInfo focuses on enterprise CRMs. kNine natively integrates with PCRecruiter, Crelate, BigBiller, and the ATS platforms recruiters actually use.' },
      { icon: '🔄', title: 'Fee Recovery Built In', desc: 'kNine\'s Enterprise plan includes Tracker, a fee recovery detection tool. ZoomInfo doesn\'t offer anything like it.' },
    ],
    comparison: [
      { f: 'Starting Price', k: '$6,000/yr', c: '$15,000+/yr' },
      { f: 'Contract Required', k: 'No', c: 'Yes (annual)' },
      { f: 'Contact Database', k: '100M+', c: '100M+' },
      { f: 'AI Natural Language Search', k: true, c: false },
      { f: 'Email Accuracy', k: '95%+', c: '90%+' },
      { f: 'Staffing ATS Integrations', k: true, c: false },
      { f: 'Fee Recovery / Tracker', k: true, c: false },
      { f: 'Chrome Extension', k: true, c: true },
      { f: 'Free Trial', k: true, c: false },
      { f: 'Real-Time Verification', k: true, c: true },
    ],
  },
  apollo: {
    name: 'Apollo.io',
    slug: 'apollo',
    tagline: 'More AI, Better Integrations, Purpose-Built for Sourcing',
    subtitle: 'Apollo.io is a solid general sales tool, but it lacks AI-powered search, staffing ATS integrations, and the recruiting-specific features your team needs. kNine was built for how you actually work.',
    painPoints: [
      { icon: '🤖', title: 'True AI Search', desc: 'Apollo uses traditional filters. kNine uses natural language AI — just describe who you\'re looking for in plain English. No boolean strings needed.' },
      { icon: '🎯', title: 'Built for Recruiting & Sales', desc: 'Apollo is sales-first. kNine is built for both recruiting and sales teams with dedicated AI agents — Roxi for recruiting, Cooper for BD.' },
      { icon: '✅', title: 'Higher Email Accuracy', desc: 'kNine delivers 95%+ email accuracy with real-time verification. Apollo reports 85%+ with no real-time validation.' },
      { icon: '🧩', title: 'Staffing ATS Integrations', desc: 'Apollo integrates with Salesforce and HubSpot. kNine also connects natively with PCRecruiter, Crelate, BigBiller, and other staffing platforms.' },
    ],
    comparison: [
      { f: 'Starting Price', k: '$6,000/yr', c: '$5,400/yr' },
      { f: 'AI Natural Language Search', k: true, c: false },
      { f: 'Email Accuracy', k: '95%+', c: '85%+' },
      { f: 'Real-Time Verification', k: true, c: false },
      { f: 'Built for Recruiting', k: true, c: false },
      { f: 'Staffing ATS Integrations', k: true, c: false },
      { f: 'Fee Recovery / Tracker', k: true, c: false },
      { f: 'Chrome Extension', k: true, c: true },
      { f: 'Free Trial', k: true, c: true },
      { f: 'No Long-Term Contract', k: true, c: true },
    ],
  },
  lusha: {
    name: 'Lusha',
    slug: 'lusha',
    tagline: 'Go Beyond a Chrome Extension — Get a Full AI Sourcing Platform',
    subtitle: 'Lusha is a great contact finder, but it\'s limited to a Chrome extension and basic search. kNine delivers a complete AI-powered sourcing platform with deeper integrations, smarter search, and more data.',
    painPoints: [
      { icon: '🤖', title: 'AI-Powered Search', desc: 'Lusha offers basic keyword filters. kNine uses natural language AI to understand context, skill adjacencies, and intent. Just describe who you need.' },
      { icon: '📊', title: 'Full Analytics Dashboard', desc: 'Lusha has no analytics. kNine gives you real-time pipeline health, team leaderboards, revenue forecasting, and AI-driven insights.' },
      { icon: '🧩', title: 'Deep ATS Integrations', desc: 'Lusha has limited CRM integrations. kNine natively connects with PCRecruiter, Crelate, BigBiller, HubSpot, Monday.com, and more.' },
      { icon: '👥', title: 'Team Collaboration', desc: 'Lusha lacks team features. kNine includes shared sourcing lists, team credit management, and team analytics on Business and Enterprise plans.' },
    ],
    comparison: [
      { f: 'Starting Price', k: '$6,000/yr', c: '$3,600/yr' },
      { f: 'Contact Database', k: '100M+', c: '100M+' },
      { f: 'AI Natural Language Search', k: true, c: false },
      { f: 'Email Accuracy', k: '95%+', c: '90%+' },
      { f: 'Analytics Dashboard', k: true, c: false },
      { f: 'Team Collaboration', k: true, c: false },
      { f: 'Staffing ATS Integrations', k: true, c: false },
      { f: 'Fee Recovery / Tracker', k: true, c: false },
      { f: 'Chrome Extension', k: true, c: true },
      { f: 'Free Trial', k: true, c: true },
    ],
  },
  seamless: {
    name: 'Seamless.AI',
    slug: 'seamless',
    tagline: 'Better AI, Better Data, No Long-Term Contracts',
    subtitle: 'Seamless.AI offers AI search, but locks you into annual contracts with unreliable data accuracy. kNine delivers higher accuracy, staffing-specific integrations, and flexible pricing.',
    painPoints: [
      { icon: '✅', title: 'Higher Data Accuracy', desc: 'Seamless.AI reports 85%+ email accuracy with no real-time verification. kNine delivers 95%+ accuracy with real-time data validation on every lookup.' },
      { icon: '📋', title: 'No Contract Lock-In', desc: 'Seamless.AI requires annual commitments. kNine offers monthly payment options and no long-term contracts. Cancel anytime.' },
      { icon: '🧩', title: 'Staffing ATS Integrations', desc: 'Seamless.AI focuses on general CRMs. kNine connects natively with PCRecruiter, Crelate, BigBiller, and other staffing-specific platforms.' },
      { icon: '🔄', title: 'Fee Recovery & Tracker', desc: 'kNine\'s Enterprise plan includes automatic fee recovery detection. Seamless.AI doesn\'t offer anything comparable.' },
    ],
    comparison: [
      { f: 'Starting Price', k: '$6,000/yr', c: '$6,000+/yr' },
      { f: 'Contract Required', k: 'No', c: 'Yes (annual)' },
      { f: 'AI Natural Language Search', k: true, c: true },
      { f: 'Email Accuracy', k: '95%+', c: '85%+' },
      { f: 'Real-Time Verification', k: true, c: false },
      { f: 'Built for Recruiting', k: true, c: false },
      { f: 'Staffing ATS Integrations', k: true, c: false },
      { f: 'Fee Recovery / Tracker', k: true, c: false },
      { f: 'Chrome Extension', k: true, c: true },
      { f: 'Free Trial', k: true, c: false },
    ],
  },
};

const renderCell = (val) => {
  if (val === true) return <span className="check-icon">✓</span>;
  if (val === false) return <span className="cross-icon">✗</span>;
  return <span>{val}</span>;
};

const CompetitorAlternative = ({ competitor }) => {
  const info = data[competitor];

  if (!info) {
    return (
      <section className="hero" style={{ minHeight: '50vh', display: 'flex', alignItems: 'center' }}>
        <div className="container hero-content text-center">
          <h1 className="hero-title">Page Not Found</h1>
          <Link to="/comparisons" className="btn btn-gradient btn-lg">View All Comparisons</Link>
        </div>
      </section>
    );
  }

  const handleTrial = (loc) => trackFreeTrial(loc);

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="container hero-content fade-up" style={{ maxWidth: '800px' }}>
          <div className="section-badge purple">vs {info.name}</div>
          <h1 className="hero-title" style={{ fontSize: '2.5rem', lineHeight: 1.15 }}>
            The #1 <span className="text-gradient">{info.name} Alternative</span> for Recruiting &amp; Sales
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1.05rem' }}>
            {info.subtitle}
          </p>
          <div className="hero-actions">
            <a
              href="https://app.knine.ai/register"
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial(`${info.slug}_hero`)}
            >
              Start Free Trial — No Credit Card
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <Link to="/pricing" className="btn btn-outline btn-lg">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section style={{ background: 'var(--white)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.75rem', marginBottom: '36px' }}>
            Why Teams Switch from {info.name} to <span className="text-gradient">kNine</span>
          </h2>
          <div className="feature-grid-2">
            {info.painPoints.map((p) => (
              <div key={p.title} className="card" style={{ padding: '28px' }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '12px' }}>{p.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {p.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Side-by-side comparison */}
      <section className="bg-off-white">
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.5rem', marginBottom: '28px' }}>
            kNine vs {info.name} — Feature Comparison
          </h2>
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '200px' }}>Feature</th>
                  <th className="highlight" style={{ minWidth: '130px' }}>kNine.ai</th>
                  <th style={{ minWidth: '130px' }}>{info.name}</th>
                </tr>
              </thead>
              <tbody>
                {info.comparison.map((row) => (
                  <tr key={row.f}>
                    <td className="feature-name">{row.f}</td>
                    <td style={{ background: '#f0f7ff' }}><strong>{renderCell(row.k)}</strong></td>
                    <td>{renderCell(row.c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section>
        <div className="container" style={{ maxWidth: '680px', textAlign: 'center' }}>
          <h2 className="section-title" style={{ fontSize: '1.6rem', marginBottom: '12px' }}>
            Ready to Make the Switch?
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
            14-day free trial. No credit card. No contracts. Start sourcing in minutes.
          </p>
          <a
            href="https://app.knine.ai/register"
            className="btn btn-gradient btn-lg"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleTrial(`${info.slug}_mid`)}
          >
            Start Free Trial
          </a>
        </div>
      </section>

      {/* See all comparisons link */}
      <section className="bg-off-white" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        <div className="container text-center">
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '12px' }}>
            Want the full picture?
          </p>
          <Link to="/comparisons" className="btn btn-outline">
            View Full Market Comparison
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Ditch {info.name}. Unleash kNine.</h2>
            <p>
              Join recruiting and sales teams who switched to kNine and never looked back.
            </p>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-white btn-lg"
              style={{ border: 'none' }}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial(`${info.slug}_bottom`)}
            >
              Start Free Trial Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompetitorAlternative;
